import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getRoles } from '../../services/roleService';

function Roles() {
  const navigate = useNavigate();
  const { isLoading } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getRoles()
      .then((data) => {
        setRoles(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch roles:', error);
        setLoading(false);
      });
  }, []);

  const handleCreateClick = useCallback(() => {
    navigate('/roles/create');
  }, [navigate]);

  const handleShowClick = useCallback((roleId) => {
    navigate(`/roles/${roleId}`);
  }, [navigate]);

  const handleEditClick = useCallback((roleId) => {
    navigate(`/roles/${roleId}/edit`);
  }, [navigate]);

  if (isLoading || loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 text-gray-500">
        <div className="w-12 h-12 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="font-medium">Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">👥 Роли</h1>
          <p className="text-gray-500">Управление ролями и правами доступа</p>
        </div>
        <button 
          onClick={handleCreateClick}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all"
        >
          ➕ Создать роль
        </button>
      </div>

      {/* Roles Table */}
      {roles.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="text-5xl mb-4 opacity-50">👤</div>
          <h2 className="text-lg font-semibold text-gray-700 mb-2">Нет ролей</h2>
          <p className="text-gray-500 mb-4">Создайте первую роль для начала работы</p>
          <button 
            onClick={handleCreateClick}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            ➕ Создать роль
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200">
                  Название
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 w-32">
                  Статус
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider border-b-2 border-gray-200 w-48">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role, index) => (
                <tr 
                  key={role.id} 
                  className={`hover:bg-gray-50 transition-colors ${index !== roles.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-lg">
                        {role.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-gray-800">{role.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {role.status ? (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                        ✓ Активный
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded-full">
                        ✗ Неактивный
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => handleShowClick(role.id)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        👁️ Просмотр
                      </button>
                      <button 
                        onClick={() => handleEditClick(role.id)}
                        className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        ✏️ Редактировать
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Roles;
